import cn from 'classnames';
import I18n from 'hooks/useI18n';
import React from 'react';

import ShowIf from 'components/common/ShowIf';
import { isActiveModule, stopEvent } from 'functions/webUtils';

import Img from 'components/common/Img';
import { useOutsideClick } from 'redhotpie-shared-lib/functions/utils';
import {
  addNoteOptionDesktop,
  icAddNoteOption,
  icBlockUserOption,
  icDeleteTrash,
  icReportOption,
} from 'redhotpie-shared-lib/themes/ImagesSVG';
import './style.scss';

interface CustomItem {
  icon?: string;
  title?: string;
  subTitle?: string;
  onClick?: () => void;
}

export interface MoreItem {
  id: string | number;
  onClick?: () => void;
  icon?: string;
  text?: string;
  subText?: string;
}

interface ProfileOptionDropDownProps {
  page?: string;
  userName?: string;
  isBlocked?: boolean;
  onBlockUser?: () => void;
  onUnBlockUser?: () => void;
  onDeleteUser?: () => void;
  isHasNotes?: boolean;
  onAddNotes?: () => void;
  onViewNotes?: () => void;
  onActionClickReport?: () => void;
  isTooltip?: boolean;
  messageTooltip?: string;
  position?: React.CSSProperties;
  showDeleteOption?: boolean;
  customItem?: CustomItem;
  className?: string;
  onCloseOption?: () => void;
  moreItems?: MoreItem[];
}

const ProfileOptionDropDown: React.FC<ProfileOptionDropDownProps> = props => {
  const {
    page = '',
    userName,
    isBlocked,
    onBlockUser,
    onUnBlockUser,
    onDeleteUser,
    isHasNotes,
    onAddNotes,
    onViewNotes,
    onActionClickReport,
    isTooltip = false,
    messageTooltip,
    position,
    showDeleteOption = true,
    customItem,
    className,
    onCloseOption,
    moreItems,
  } = props;

  const customClassWrapper = cn('profile-option-drop-down', {
    [className || '']: className,
  });

  const styleLiEnd: React.CSSProperties = { borderRadius: '8px' };

  const refProfileOptions = useOutsideClick(() => {
    if (typeof onCloseOption === 'function') {
      onCloseOption();
    }
  });

  const onUnOrBlockUser = (e: React.MouseEvent) => {
    stopEvent(e);
    if (isBlocked) {
      if (typeof onUnBlockUser === 'function') {
        onUnBlockUser();
      }
    } else {
      if (typeof onBlockUser === 'function') {
        onBlockUser();
      }
    }
  };

  return (
    <div className={customClassWrapper} ref={refProfileOptions} style={{ ...position }}>
      {isTooltip && <div className="__as-tooltip">{messageTooltip}</div>}
      {!isTooltip && (
        <ul>
          <li onClick={onUnOrBlockUser}>
            <Img src={isBlocked ? addNoteOptionDesktop : icBlockUserOption} alt="addNote" />
            <div className="desc-profile-option-drop-down">
              <span className="heading">{I18n.t(`MemberProfileScreen.${isBlocked ? 'UnBlockUser' : 'BlockUser'}`)}</span>
              <span>{I18n.t(`MemberProfileScreen.${isBlocked ? 'UnBlockUserSubTitle' : 'BlockUserSubTitle'}`, { userName })}</span>
            </div>
          </li>
          <ShowIf condition={isActiveModule.prospect(page) && showDeleteOption}>
            <li onClick={onDeleteUser} style={{ ...styleLiEnd }}>
              <Img src={icDeleteTrash} alt="icDeleteGrayProspect" />
              <div className="desc-profile-option-drop-down">
                <span className="heading">{I18n.t('Btn.Delete')}</span>
                <span>
                  {I18n.t('Prospects.ThisWillRemoveFromTheList', {
                    userName,
                  })}
                </span>
              </div>
            </li>
          </ShowIf>
          <ShowIf condition={!isActiveModule.prospect(page)}>
            {moreItems &&
              moreItems.map(itm => (
                <li key={itm.id} onClick={itm.onClick}>
                  <Img alt={itm.icon ? 'icon' : ''} src={itm.icon} />
                  <div className="desc-profile-option-drop-down">
                    <span className="heading">{itm.text}</span>
                    <span>{itm.subText}</span>
                  </div>
                </li>
              ))}
            <li onClick={isHasNotes ? onViewNotes : onAddNotes}>
              <Img src={icAddNoteOption} alt="icAddNoteOption" />
              <div className="desc-profile-option-drop-down">
                <span className="heading">{I18n.t(`MemberProfileScreen.${isHasNotes ? 'ViewOrAddNotes' : 'AddNotes'}`)}</span>
                <span>{I18n.t(`MemberProfileScreen.${isHasNotes ? 'ViewNotesSubTitle' : 'AddNotesSubTitle'}`, { userName })}</span>
              </div>
            </li>
            <li onClick={onActionClickReport} style={{ ...styleLiEnd }}>
              <Img src={icReportOption} alt="reportOptionDesktop" />
              <div className="desc-profile-option-drop-down">
                <span className="heading">{I18n.t('MemberProfileScreen.Report')}</span>
                <span>{I18n.t('MemberProfileScreen.ReportSubTitle', { userName })}</span>
              </div>
            </li>
          </ShowIf>
          {customItem && (
            <li onClick={customItem.onClick} style={{ ...styleLiEnd }}>
              <Img alt={customItem.icon ? 'icon' : ''} src={customItem.icon} />
              <div className="desc-profile-option-drop-down">
                <span className="heading">{customItem.title}</span>
                <span>{customItem.subTitle}</span>
              </div>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default ProfileOptionDropDown;
